import { useEffect, useRef, useState } from 'react';

export interface MenuItem {
  readonly label: string;
  readonly onSelect: () => void;
  readonly disabled?: boolean;
  /** Atajo que se muestra a la derecha; solo informativo si no está implementado en la página. */
  readonly hint?: string;
}

export interface MenuDef {
  readonly label: string;
  readonly items: readonly MenuItem[];
}

interface Props {
  readonly menus: readonly MenuDef[];
}

/** Barra de menús con el patrón ARIA de «menubar»: flechas, Inicio/Fin, Esc y cierre al salir. */
export function MenuBar({ menus }: Props): JSX.Element {
  const [open, setOpen] = useState<number | undefined>();
  const barRef = useRef<HTMLDivElement>(null);
  const focusFirst = useRef(false);

  const items = (menu: number): HTMLButtonElement[] =>
    Array.from(barRef.current?.querySelectorAll<HTMLButtonElement>(`[data-menu="${menu}"] [role="menuitem"]:not(:disabled)`) ?? []);
  const trigger = (menu: number): HTMLButtonElement | null => barRef.current?.querySelector<HTMLButtonElement>(`[data-trigger="${menu}"]`) ?? null;

  useEffect(() => {
    if (open === undefined) return;
    if (focusFirst.current) {
      focusFirst.current = false;
      items(open)[0]?.focus();
    }
    const onDown = (e: MouseEvent): void => {
      if (!barRef.current?.contains(e.target as Node)) setOpen(undefined);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const openMenu = (menu: number, focusItem: boolean): void => {
    focusFirst.current = focusItem;
    setOpen(menu);
  };
  const close = (returnFocus: number | undefined): void => {
    setOpen(undefined);
    if (returnFocus !== undefined) trigger(returnFocus)?.focus();
  };

  const onTriggerKey = (e: React.KeyboardEvent, menu: number): void => {
    const last = menus.length - 1;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      openMenu(menu, true);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const next = e.key === 'ArrowRight' ? (menu === last ? 0 : menu + 1) : menu === 0 ? last : menu - 1;
      trigger(next)?.focus();
      if (open !== undefined) openMenu(next, false);
    } else if (e.key === 'Escape') close(menu);
  };

  const onItemKey = (e: React.KeyboardEvent, menu: number): void => {
    const list = items(menu);
    const at = list.findIndex((b) => b === document.activeElement);
    const go = (i: number): void => {
      e.preventDefault();
      list[(i + list.length) % list.length]?.focus();
    };
    if (e.key === 'ArrowDown') go(at + 1);
    else if (e.key === 'ArrowUp') go(at - 1);
    else if (e.key === 'Home') go(0);
    else if (e.key === 'End') go(list.length - 1);
    else if (e.key === 'Escape') {
      e.preventDefault();
      close(menu);
    } else if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      const last = menus.length - 1;
      const next = e.key === 'ArrowRight' ? (menu === last ? 0 : menu + 1) : menu === 0 ? last : menu - 1;
      trigger(next)?.focus();
      openMenu(next, true);
    }
  };

  return (
    <div
      className="menubar"
      role="menubar"
      aria-label="Menú principal"
      ref={barRef}
      onBlur={(e) => {
        if (open !== undefined && !barRef.current?.contains(e.relatedTarget as Node | null)) setOpen(undefined);
      }}
    >
      {menus.map((m, mi) => (
        <div className="menubar__menu" key={m.label}>
          <button
            type="button"
            role="menuitem"
            className="menubar__trigger"
            data-trigger={mi}
            aria-haspopup="menu"
            aria-expanded={open === mi}
            onClick={() => (open === mi ? close(undefined) : openMenu(mi, false))}
            onMouseEnter={() => open !== undefined && open !== mi && openMenu(mi, false)}
            onKeyDown={(e) => onTriggerKey(e, mi)}
          >
            {m.label}
          </button>
          {open === mi && (
            <div className="menubar__popup" role="menu" aria-label={m.label} data-menu={mi}>
              {m.items.map((it) => (
                <button
                  key={it.label}
                  type="button"
                  role="menuitem"
                  disabled={it.disabled}
                  onKeyDown={(e) => onItemKey(e, mi)}
                  onClick={() => {
                    close(mi);
                    it.onSelect();
                  }}
                >
                  <span>{it.label}</span>
                  {it.hint !== undefined && <span className="menubar__hint">{it.hint}</span>}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
